import React, {useEffect, useRef} from "react";

const ITEM_HEIGHT = 40;

const enableDragToScroll = (el) => {
  if (!el) {return;}
  let isDragging = false;
  let startY;
  let scrollTop;

  const handleDragStart = (e) => {
    isDragging = true;
    startY = e.pageY;
    scrollTop = el.scrollTop;
    el.style.cursor = "grabbing";
  };

  const handleDragMove = (e) => {
    if (!isDragging) {return;}
    e.preventDefault();
    const y = e.pageY;
    const walk = startY - y;
    el.scrollTop = scrollTop + walk;
  };

  const handleDragEnd = () => {
    if (!isDragging) {return;}
    isDragging = false;
    el.style.cursor = "grab";
  };

  el.addEventListener("mousedown", handleDragStart);
  window.addEventListener("mousemove", handleDragMove);
  window.addEventListener("mouseup", handleDragEnd);
  el.style.cursor = "grab";

  return () => {
    el.removeEventListener("mousedown", handleDragStart);
    window.removeEventListener("mousemove", handleDragMove);
    window.removeEventListener("mouseup", handleDragEnd);
  };
};

const rerenderElements = (el) => {
  if (!el) {return;}
  const items = Array.from(el.querySelectorAll(".wheel-item"));
  const scrollTop = el.scrollTop;
  const centerPosition = scrollTop + el.clientHeight / 2;

  items.forEach((item) => {
    const itemCenter = item.offsetTop + item.offsetHeight / 2;
    const distanceFromCenter = itemCenter - centerPosition;

    let rotateX = (distanceFromCenter / item.offsetHeight) * -25;
    rotateX = Math.max(-80, Math.min(80, rotateX));

    const absDistance = Math.abs(distanceFromCenter);
    const opacity = Math.max(0.2, 1 - absDistance / (el.clientHeight / 1.2));
    const scale = Math.max(0.8, 1 - absDistance / (el.clientHeight * 2));

    item.style.transform = `rotateX(${rotateX}deg) scale(${scale})`;
    item.style.opacity = opacity;
  });
};

const WheelColumn = ({items, selectedValue, onChange, width = "100px"}) => {
  const listRef = useRef(null);
  const scrollTimeoutRef = useRef(null);
  const isProgrammaticScroll = useRef(false);

  useEffect(() => {
    const listElement = listRef.current;
    if (!listElement) {return;}

    const handleWheelEvent = (e) => {
      e.preventDefault();
      const currentIndex = items.findIndex((item) => String(item.value) === String(selectedValue));
      if (currentIndex === -1) {return;}

      if (e.deltaY > 0 && currentIndex < items.length - 1) {
        onChange(items[currentIndex + 1].value);
      } else if (e.deltaY < 0 && currentIndex > 0) {
        onChange(items[currentIndex - 1].value);
      }
    };
    listElement.addEventListener("wheel", handleWheelEvent, {passive: false});

    return () => {
      listElement.removeEventListener("wheel", handleWheelEvent);
    };
  }, [items, selectedValue, onChange]);

  useEffect(() => {
    return enableDragToScroll(listRef.current);
  }, []);

  useEffect(() => {
    if (listRef.current) {
      const index = items.findIndex((item) => String(item.value) === String(selectedValue));
      if (index !== -1) {
        const targetScrollTop = index * ITEM_HEIGHT;

        if (Math.abs(listRef.current.scrollTop - targetScrollTop) > ITEM_HEIGHT / 2) {
          isProgrammaticScroll.current = true;
          listRef.current.scrollTop = targetScrollTop;
          rerenderElements(listRef.current);

          setTimeout(() => {
            isProgrammaticScroll.current = false;
          }, 100);
        } else {
          rerenderElements(listRef.current);
        }
      }
    }
  }, [selectedValue, items]);

  const handleScroll = (e) => {
    const el = e.target;

    window.requestAnimationFrame(() => {
      rerenderElements(el);
    });

    if (isProgrammaticScroll.current) {return;}

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      const selectedIndex = Math.round(el.scrollTop / ITEM_HEIGHT);
      if (items[selectedIndex] && String(items[selectedIndex].value) !== String(selectedValue)) {
        onChange(items[selectedIndex].value);
      }
    }, 150);
  };

  return (
    <div style={{position: "relative", width, height: ITEM_HEIGHT * 5, overflow: "hidden"}}>
      <div style={{
        position: "absolute", top: "50%", left: 0, right: 0,
        height: ITEM_HEIGHT, marginTop: -ITEM_HEIGHT / 2,
        borderTop: "1px solid #ccc", borderBottom: "1px solid #ccc",
        pointerEvents: "none", zIndex: 2,
      }} />

      <ul
        ref={listRef}
        onScroll={handleScroll}
        style={{
          margin: 0,
          padding: 0,
          height: "100%",
          overflowY: "scroll",
          scrollSnapType: "y mandatory",
          listStyle: "none",
          scrollbarWidth: "none",
          MsOverflowStyle: "none",
          perspective: "1000px",
        }}
        className="hide-scrollbar"
      >
        <li style={{height: ITEM_HEIGHT * 2, flexShrink: 0}} />

        {items.map((item, index) => (
          <li
            key={index}
            onClick={() => onChange(item.value)}
            className="wheel-item"
            style={{
              height: ITEM_HEIGHT,
              lineHeight: `${ITEM_HEIGHT}px`,
              textAlign: "center",
              scrollSnapAlign: "center",
              fontSize: "16px",
              userSelect: "none",
              transformOrigin: "center center -50px",
              transition: "transform 0.1s ease-out, opacity 0.1s ease-out",
            }}
          >
            {item.label}
          </li>
        ))}

        <li style={{height: ITEM_HEIGHT * 2, flexShrink: 0}} />
      </ul>
      <style dangerouslySetInnerHTML={{
        __html: `
        .hide-scrollbar::-webkit-scrollbar { display: none; }
      `}} />
    </div>
  );
};

export const WheelPicker = ({year, month, day, onChangeYear, onChangeMonth, onChangeDay}) => {
  const yearItems = Array.from({length: 1405 - 1300 + 1}, (_, i) => ({value: 1300 + i, label: 1300 + i}));

  const monthNames = ["فروردین", "اردیبهشت", "خرداد", "تیر", "مرداد", "شهریور", "مهر", "آبان", "آذر", "دی", "بهمن", "اسفند"];
  const monthItems = monthNames.map((name, i) => ({value: i + 1, label: name}));

  const dayItems = Array.from({length: 31}, (_, i) => ({value: i + 1, label: i + 1}));

  return (
    <div style={{display: "flex", justifyContent: "center", direction: "ltr", borderRadius: "4px", padding: "10px", background: "rgb(255 255 255 / 50%)", borderColor: "#d9d9d9"}}>
      <WheelColumn items={yearItems} selectedValue={year} onChange={onChangeYear} width="80px" />
      <WheelColumn items={monthItems} selectedValue={month} onChange={onChangeMonth} width="100px" />
      <WheelColumn items={dayItems} selectedValue={day} onChange={onChangeDay} width="60px" />
    </div>
  );
};

export default WheelPicker;
